import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { SelectableIcons } from '@frontend/shared';
import { createShallowStore } from '@frontend/store';
import { dispatchTopBarModalClose, dispatchTopBarModalOpen } from '../events/modal-events';

export type TopBarSelectableIcons = SelectableIcons | '';

interface TopBarState {
  selectedIcon: TopBarSelectableIcons;
  isSchedulePulseOpen: boolean;
  isQuickFillOpen: boolean;
  isNotificationsOpen: boolean;
  isNotificationsHovering: boolean;
  isActionMobileOpen: boolean;
  nextVersion: string | undefined;
  setSelectedIcon: (icon: TopBarSelectableIcons) => void;
  toggleSelectedIcon: (icon: TopBarSelectableIcons) => void;
  setSchedulePulseOpen: (open: boolean) => void;
  setQuickFillOpen: (open: boolean) => void;
  setNotificationsOpen: (open: boolean) => void;
  setNotificationsHovering: (isHovering: boolean) => void;
  setNextVersion: (nextVersion: string | undefined) => void;
  setActionMobileOpen: (open: boolean) => void;
  closeAll: () => void;
  handleSelectableIconClick: (icon: TopBarSelectableIcons, evt?: any) => void;
}

export const useTopBarStore = create<TopBarState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        selectedIcon: '',
        isSchedulePulseOpen: false,
        isQuickFillOpen: false,
        isNotificationsOpen: false,
        nextVersion: undefined,
        isNotificationsHovering: false,
        isActionMobileOpen: false,
        setSelectedIcon: (icon) => set({ selectedIcon: icon }),
        toggleSelectedIcon: (icon) => {
          const { selectedIcon } = get();
          set({ selectedIcon: selectedIcon === icon ? '' : icon });
        },
        setSchedulePulseOpen: (open) => set({ isSchedulePulseOpen: open }),
        setQuickFillOpen: (open) => set({ isQuickFillOpen: open }),
        setNotificationsOpen: (open) => set({ isNotificationsOpen: open }),
        setActionMobileOpen: (open) => set({ isActionMobileOpen: open }),
        setNextVersion: (nextVersion) => set({ nextVersion }),
        setNotificationsHovering: (isHovering) => set({ isNotificationsHovering: isHovering }),
        closeAll: () =>
          set({
            selectedIcon: '',
            isSchedulePulseOpen: false,
            isQuickFillOpen: false,
            isNotificationsOpen: false,
            isActionMobileOpen: false,
          }),
        handleSelectableIconClick: (icon, evt) => {
          evt?.stopPropagation?.();
          if (icon === 'taskTray') return;
          const { selectedIcon } = get();
          if (icon === selectedIcon) {
            dispatchTopBarModalClose();
          } else {
            set({ selectedIcon: icon });
            dispatchTopBarModalOpen();
            if (icon === 'schedulePulse') set({ isSchedulePulseOpen: true });
            if (icon === 'quickfill') set({ isQuickFillOpen: true });
          }
        },
      }),
      { name: 'top-bar-store', store: 'top-bar', trace: true }
    )
  )
);

export const useTopBarShallowStore = createShallowStore(useTopBarStore);
